/**
 * Role-aware navigation destinations for the global voice assistant ("Jarvis").
 *
 * The frontend owns the route table, so we build the list here and send it to the
 * backend in `formState.navOptions`. Each option: { label, route, keywords }.
 * Routes mirror src/App.js and src/components/Sidebar.js.
 */

const PROFILE = { label: 'Profile', route: '/profile', keywords: ['profile', 'account', 'my account'] };

export function getNavOptions(role) {
  switch (role) {
    case 'superadmin':
      return [
        { label: 'Dashboard', route: '/super-admin', keywords: ['dashboard', 'home'] },
        { label: 'User Management', route: '/super-admin/user-management', keywords: ['user management', 'users', 'coordinators', 'permissions', 'manage users'] },
        { label: 'Subjects', route: '/super-admin/subjects', keywords: ['subjects', 'subject'] },
        { label: 'Activity Log', route: '/super-admin/activity-log', keywords: ['activity log', 'audit', 'logs', 'activity'] },
        { label: 'Token Ledger', route: '/super-admin/token-ledger', keywords: ['token ledger', 'tokens', 'token', 'usage'] },
        { label: 'Question Insights', route: '/super-admin/question-insights', keywords: ['question insights', 'insights', 'analytics'] },
        { label: 'Manage Categorized Sets', route: '/super-admin/categorized-sets', keywords: ['categorized sets', 'sets', 'categorized'] },
        { label: 'Paper Management', route: '/super-admin/paper-management', keywords: ['paper management', 'papers', 'manage papers'] },
        { label: 'AI Provider', route: '/super-admin/ai-provider', keywords: ['ai provider', 'ai settings', 'provider', 'ai'] },
        { label: 'EduLab PDFs', route: '/super-admin/edulab-pdfs', keywords: ['edulab', 'pdf', 'pdfs', 'library'] },
        PROFILE,
      ];
    case 'cetadmin':
      return [
        { label: 'Dashboard', route: '/super-admin', keywords: ['dashboard', 'home'] },
        { label: 'User Management', route: '/super-admin/user-management', keywords: ['user management', 'users', 'coordinators', 'permissions'] },
        { label: 'Subjects', route: '/super-admin/subjects', keywords: ['subjects'] },
        { label: 'Activity Log', route: '/super-admin/activity-log', keywords: ['activity log', 'audit', 'logs'] },
        { label: 'Token Ledger', route: '/super-admin/token-ledger', keywords: ['token ledger', 'tokens', 'usage'] },
        { label: 'Question Insights', route: '/super-admin/question-insights', keywords: ['question insights', 'insights', 'analytics'] },
        { label: 'Manage Categorized Sets', route: '/super-admin/categorized-sets', keywords: ['categorized sets', 'sets'] },
        { label: 'Paper Management', route: '/super-admin/paper-management', keywords: ['paper management', 'papers'] },
        PROFILE,
      ];
    case 'cetcoordinator':
      return [
        { label: 'Dashboard', route: '/coordinator', keywords: ['dashboard', 'home'] },
        { label: 'User Management', route: '/coordinator/users', keywords: ['user management', 'users'] },
        { label: 'Subjects', route: '/coordinator/subjects', keywords: ['subjects'] },
        { label: 'Activity Log', route: '/coordinator/activity-log', keywords: ['activity log', 'logs'] },
        { label: 'Question Insights', route: '/coordinator/question-insights', keywords: ['insights', 'analytics'] },
        { label: 'Token Ledger', route: '/coordinator/token-ledger', keywords: ['token ledger', 'tokens'] },
        { label: 'Categorized Sets', route: '/coordinator/categorized-sets', keywords: ['categorized sets', 'sets'] },
        { label: 'Paper Management', route: '/coordinator/paper-management', keywords: ['paper management', 'papers'] },
        PROFILE,
      ];
    case 'moderator':
      return [
        { label: 'Dashboard', route: '/moderator', keywords: ['dashboard', 'home'] },
        { label: 'Categorization', route: '/moderator-categorization', keywords: ['categorization', 'categorize', 'review papers', 'papers', 'sets'] },
        { label: 'Approvals', route: '/moderator/approvals', keywords: ['approvals', 'approve', 'finalized'] },
        PROFILE,
      ];
    case 'subject_matter_expert':
      return [
        { label: 'Dashboard', route: '/subject-matter-expert', keywords: ['dashboard', 'home'] },
        { label: 'SME Selection', route: '/sme-selection', keywords: ['selection', 'sme selection', 'select sets', 'pick'] },
        { label: 'Papers', route: '/sme-papers', keywords: ['papers', 'review', 'variations'] },
        { label: 'Sub-question Reviews', route: '/sme/sub-question-reviews', keywords: ['sub question', 'reviews', 'sub-question'] },
        PROFILE,
      ];
    case 'examiner':
      return [
        { label: 'Dashboard', route: '/examiner', keywords: ['dashboard', 'home'] },
        { label: 'Create Paper', route: '/examiner/create', keywords: ['create', 'generate', 'new paper', 'create paper', 'generate questions'] },
        { label: 'My Papers', route: '/examiner/papers', keywords: ['my papers', 'papers', 'view papers'] },
        { label: 'Paper Templates', route: '/paper-templates', keywords: ['templates', 'template'] },
        { label: 'Extract Template', route: '/extract-template', keywords: ['extract', 'extract template', 'from pdf'] },
        PROFILE,
      ];
    default:
      return [PROFILE];
  }
}

/** Human label of the page the user is currently on (longest route-prefix match). */
export function currentPageLabel(role, pathname) {
  const opts = getNavOptions(role);
  const match = opts
    .filter((o) => pathname === o.route || pathname.startsWith(o.route + '/'))
    .sort((a, b) => b.route.length - a.route.length)[0];
  return match ? match.label : null;
}
